let googleMapsPromise = null;

// Injects the official Google Maps JS API bootstrap loader once. This defines
// `google.maps.importLibrary` immediately (handling async loading correctly),
// unlike a plain `<script>` tag where the namespace isn't ready on `onload`.
function bootstrapGoogleMaps(apiKey) {
    if (window.google?.maps?.importLibrary) {
        return;
    }

    // Adapted from Google's documented "Dynamic Library Import" bootstrap snippet.
    ((g) => {
        let h;
        let a;
        let k;
        const p = 'The Google Maps JavaScript API';
        const c = 'google';
        const l = 'importLibrary';
        const q = '__ib__';
        const m = document;
        let b = window;
        b = b[c] || (b[c] = {});
        const d = b.maps || (b.maps = {});
        const r = new Set();
        const e = new URLSearchParams();
        const u = () => h || (h = new Promise(async (f, n) => {
            a = m.createElement('script');
            e.set('libraries', [...r] + '');
            for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
            e.set('callback', c + '.maps.' + q);
            a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
            d[q] = f;
            a.onerror = () => { h = n(Error(p + ' could not load.')); };
            a.nonce = m.querySelector('script[nonce]')?.nonce || '';
            m.head.append(a);
        }));
        if (d[l]) {
            console.warn(p + ' only loads once. Ignoring:', g);
        } else {
            d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
        }
    })({ key: apiKey, v: 'weekly' });
}

// Loads the Google Maps Places library exactly once and resolves with the Places
// library object (which contains PlaceAutocompleteElement). Safe to call concurrently.
export function loadGooglePlaces(apiKey) {
    if (googleMapsPromise) {
        return googleMapsPromise;
    }

    googleMapsPromise = (async () => {
        bootstrapGoogleMaps(apiKey);
        return window.google.maps.importLibrary('places');
    })().catch((error) => {
        // Reset so a later attempt can retry rather than reusing a rejected promise.
        googleMapsPromise = null;
        throw error;
    });

    return googleMapsPromise;
}

// Normalizes a Place from the new PlaceAutocompleteElement API (addressComponents
// with longText/shortText, location LatLng) into the legacy Autocomplete shape
// (address_components with long_name/short_name, geometry.location.lat()/lng()) so
// existing consumers like convertAddressComponentsToNamedProperties keep working.
export function normalizePlace(place) {
    const components = (place?.addressComponents ?? []).map(component => ({
        long_name: component.longText,
        short_name: component.shortText,
        types: component.types,
    }));

    const location = place?.location ?? null;

    return {
        address_components: components,
        geometry: {
            location: {
                lat: () => (typeof location?.lat === 'function' ? location.lat() : location?.lat),
                lng: () => (typeof location?.lng === 'function' ? location.lng() : location?.lng),
            },
        },
    };
}

export function convertAddressComponentsToNamedProperties(place) {
    let address_properties = {};

    if ( !place?.address_components ) {
        return;
    }

    for (const address_component of place.address_components) {
        if (!address_component?.types) {
            continue;
        }

        for (const type of address_component.types) {
            if (!address_component?.long_name && !address_component?.short_name) {
                continue;
            }

            address_properties[type] = {};

            if (address_component?.long_name) {
                address_properties[type]['long_name'] = address_component.long_name;
            }

            if (address_component?.short_name) {
                address_properties[type]['short_name'] = address_component.short_name;
            }
        }
    }

    return address_properties;
}